import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import PortalLayout from "@/components/PortalLayout";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Plus, ClipboardList } from "lucide-react";

const statusColors = {
  pending_review: { bg: "#FEF3C7", text: "#D97706" },
  in_progress: { bg: "#DBEAFE", text: "#1D4ED8" },
  completed: { bg: "#D1FAE5", text: "#059669" },
  cancelled: { bg: "#FEE2E2", text: "#DC2626" },
  on_hold: { bg: "#F3E8FF", text: "#7C3AED" },
};

const fmt = (s) => s?.split("_").map((w) => w[0].toUpperCase() + w.slice(1)).join(" ") || "";

export default function PortalDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [meData, setMeData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/portal/me"),
      api.get("/portal/requests"),
    ]).then(([me, req]) => {
      setMeData(me.data);
      setRequests(req.data);
    }).finally(() => setLoading(false));
  }, []);

  return (
    <PortalLayout>
      <div data-testid="portal-dashboard">
        {/* Header */}
        <div className="flex items-start justify-between mb-8">
          <div>
            <h1 className="font-serif text-3xl font-bold mb-1" style={{ color: "#292524" }}>
              Welcome back, {user?.name?.split(" ")[0]}
            </h1>
            {meData?.client && (
              <p className="text-sm" style={{ color: "#57534E" }}>{meData.client.name}</p>
            )}
          </div>
          <Link
            to="/portal/requests/new"
            data-testid="portal-new-request-button"
            className="flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium text-sm transition-opacity hover:opacity-90"
            style={{ background: "#D97706", color: "#fff" }}
          >
            <Plus size={15} />
            New Task
          </Link>
        </div>

        {/* Requests List */}
        <div className="bg-white rounded-xl border overflow-hidden" style={{ borderColor: "#E7E5E4" }}>
          <div className="px-6 py-4 border-b flex items-center justify-between" style={{ borderColor: "#E7E5E4" }}>
            <h2 className="font-serif text-base font-semibold" style={{ color: "#292524" }}>
              Tasks {!loading && <span className="font-sans text-sm font-normal" style={{ color: "#A8A29E" }}>({requests.length})</span>}
            </h2>
            <Link to="/portal/requests" className="text-xs font-medium" style={{ color: "#D97706" }}>View all</Link>
          </div>
          {loading ? (
            <div className="py-8 text-center text-sm" style={{ color: "#A8A29E" }}>Loading...</div>
          ) : requests.length === 0 ? (
            <div className="py-12 text-center">
              <ClipboardList size={28} className="mx-auto mb-3" style={{ color: "#E7E5E4" }} />
              <p className="text-sm mb-2" style={{ color: "#A8A29E" }}>No tasks yet</p>
              <Link to="/portal/requests/new" className="text-sm font-medium" style={{ color: "#D97706" }}>Submit your first task</Link>
            </div>
          ) : (
            requests.slice(0, 8).map((req) => {
              const sc = statusColors[req.status] || { bg: "#F5F3EF", text: "#57534E" };
              return (
                <div
                  key={req.id}
                  data-testid={`dashboard-request-${req.id}`}
                  onClick={() => navigate(`/portal/requests/${req.id}`)}
                  className="px-6 py-4 border-b last:border-b-0 flex items-center justify-between cursor-pointer hover:bg-stone-50 transition-colors"
                  style={{ borderColor: "#F5F3EF" }}
                >
                  <div>
                    <p className="text-sm font-medium" style={{ color: "#292524" }}>{req.title}</p>
                    <p className="text-xs mt-0.5" style={{ color: "#A8A29E" }}>{req.created_at?.slice(0, 10)}</p>
                  </div>
                  <span className="ml-4 flex-shrink-0 text-xs font-medium px-2.5 py-1 rounded-full" style={{ background: sc.bg, color: sc.text }}>
                    {fmt(req.status)}
                  </span>
                </div>
              );
            })
          )}
        </div>
      </div>
    </PortalLayout>
  );
}
